 'use client';

 import { createContext, useContext, ReactNode, useCallback, useEffect, useState } from 'react';
 import { useSelector } from 'react-redux';
 import type { RootState } from '@/store';
 import {
  clearUser as clearUserState,
  logoutUser,
  setUser,
  setUserLoading,
  fetchMyTeams,
  fetchCurrentTeam,
} from '@/store';
 import { fetchPointsBalance } from '@/store/slices/pointsSlice';
// import { fetchNotifications } from '@/store/slices/notificationSlice';
import { storage, getUserInfo } from '@/lib/auth';
import { isUnauthorizedAuthSessionError } from '@/lib/authSession';
import { setBaiduPartner, setBaiduUserId } from '@/lib/baidu_hm';
import { getPartnerFromSearch } from '@/lib/partner';
import { reportPartnerAttribution } from '@/lib/partnerAttributionReporter';
import { useAppDispatch } from '@/hooks/redux';
 import type { User } from '@/lib/types/user';

const AUTH_BOOTSTRAP_MAX_ATTEMPTS = 3;
const AUTH_BOOTSTRAP_RETRY_DELAY_MS = 1500;

interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  loading: boolean;
  authChecked: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const dispatch = useAppDispatch();
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const userLoading = useSelector((state: RootState) => state.user.isLoading);

  const isLoggedIn = !!currentUser;
  const [authChecked, setAuthChecked] = useState(false);

  const syncTeamContext = useCallback(async () => {
    await Promise.allSettled([
      dispatch(fetchMyTeams()).unwrap(),
      dispatch(fetchCurrentTeam()).unwrap(),
    ]);
  }, [dispatch]);

  const hydrateAuthenticatedUser = useCallback(async (userId: string) => {
    const userInfo = await getUserInfo(userId);

    if (!userInfo) {
      throw new Error('No user info found');
    }

    dispatch(setUser(userInfo));
    setBaiduUserId(userId);
    setBaiduPartner(getPartnerFromSearch());
    dispatch(fetchPointsBalance());
    // dispatch(fetchNotifications({}));
    await syncTeamContext();
  }, [dispatch, syncTeamContext]);

  useEffect(() => {
    let disposed = false;
    let retryTimeoutId: number | null = null;

    const finalizeAuthCheck = () => {
      if (disposed) {
        return;
      }

      dispatch(setUserLoading(false));
      setAuthChecked(true);
    };

    const clearInvalidAuth = () => {
      storage.clearAuthData();
      dispatch(clearUserState());
      finalizeAuthCheck();
    };

    const bootstrapUser = async (userId: string, attempt: number) => {
      try {
        await hydrateAuthenticatedUser(userId);

        if (!disposed) {
          setAuthChecked(true);
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);

        if (disposed) {
          return;
        }

        if (isUnauthorizedAuthSessionError(error)) {
          clearInvalidAuth();
          return;
        }

        if (attempt < AUTH_BOOTSTRAP_MAX_ATTEMPTS) {
          retryTimeoutId = window.setTimeout(() => {
            void bootstrapUser(userId, attempt + 1);
          }, AUTH_BOOTSTRAP_RETRY_DELAY_MS * attempt);
          return;
        }

        finalizeAuthCheck();
      }
    };

    // Check if user is logged in via localStorage
    if (storage.isLoggedIn()) {
      const { userId } = storage.getAuthData();
      if (userId && !currentUser) {
        void bootstrapUser(userId, 1);
      } else {
        finalizeAuthCheck();
      }
    } else {
      // Not logged in, clear user state and stop loading
      if (currentUser) {
        dispatch(clearUserState());
      }
      finalizeAuthCheck();
    }
    return () => {
      disposed = true;
      if (retryTimeoutId !== null) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [currentUser, dispatch, hydrateAuthenticatedUser]);

  useEffect(() => {
    if (currentUser?.user_id) {
      void reportPartnerAttribution(currentUser.user_id);
    }
  }, [currentUser?.user_id]);

  const login = useCallback(async () => {
    // This method is kept for backward compatibility
    // Actual login should be handled by authenticateWithInvitation
    if (storage.isLoggedIn()) {
      const { userId } = storage.getAuthData();
      if (userId) {
        try {
          await hydrateAuthenticatedUser(userId);
        } catch (error) {
          console.error('Login error:', error);
          if (isUnauthorizedAuthSessionError(error)) {
            storage.clearAuthData();
          }
        }
      }
    }
  }, [hydrateAuthenticatedUser]);

  const logout = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [dispatch]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, user: currentUser, loading: userLoading, authChecked, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
